import { Checkbox, Row } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '@portal/services/i18n';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import MultipleSelect from '@portal/components/AdvancedMultipleSelect/AdvancedMultipleSelect';
import TrashIcon from '@portal/assets/svg/trash_icon.svg?react';
import PlusIcon from '@portal/assets/svg/plus_icon.svg?react';
import { RemitInputs } from '../RemitModal';
import { maskMoney } from '@portal/services/masks';

interface Form {
  index: number;
  item: RemitInputs;
  rows: RemitInputs[];
  addRow: (index: number) => void;
  deleteRow: (index: number) => void;
  onChange: (
    index: number,
    type: 'min' | 'max' | 'minApprovers',
    text: string
  ) => void;
  options: { label: string; value: string }[];
  handleChange: (option: string[], index: number) => void;
}

const Form: React.FC<Form> = ({
  item,
  index,
  rows,
  options,
  addRow,
  deleteRow,
  onChange,
  handleChange,
}) => {
  const [checkInfinity, setCheckInfinity] = useState(true);

  const lastIndex = rows.length === index + 1;
  const notLast = index + 1 < rows.length;

  useEffect(() => {
    if (checkInfinity) {
      onChange(index, 'max', '');
    }
  }, [checkInfinity, onChange, index]);

  const newRow = useMemo(
    () =>
      item?.approvers.length > 0 &&
      item.approvers.length >= Number(item.minApprovers) &&
      item?.maxLimit &&
      item?.minApprovers &&
      item.maxLimit !== 0,

    [item.approvers.length, item.maxLimit, item.minApprovers]
  );

  return (
    <Row className="remit-modal__row" key={index}>
      <span className="remit-modal__row__space">
        <AdvancedInput
          disabled
          value={maskMoney(item.minLimit.toFixed(2))}
          onChange={(text) => onChange(index, 'min', text)}
          label={translate('PAGES.PROFILE_MANAGER.REMIT.MIN_LIMIT')}
        />
      </span>

      <span className="remit-modal__row__space">
        <AdvancedInput
          value={maskMoney(item.maxLimit.toFixed(2))}
          onBlur={() => {
            if (lastIndex && !checkInfinity && newRow) {
              addRow(index);
            }
          }}
          onChange={(text) => onChange(index, 'max', maskMoney(text))}
          label={translate('PAGES.PROFILE_MANAGER.REMIT.MAX_LIMIT')}
          disabled={checkInfinity || notLast}
        />
        {lastIndex ? (
          <Checkbox
            style={{ position: 'absolute', top: 18, right: 7 }}
            checked={checkInfinity}
            onChange={() => setCheckInfinity(!checkInfinity)}
          />
        ) : null}
      </span>

      <MultipleSelect
        disabled={notLast}
        index={index}
        placeholder={translate('PAGES.PROFILE_MANAGER.REMIT.APPROVERS')}
        names={options}
        onChange={handleChange}
      />

      <span className="remit-modal__row__space">
        <AdvancedInput
          disabled={notLast}
          type="number"
          value={item.minApprovers}
          onChange={(text) => onChange(index, 'minApprovers', text)}
          label={translate('PAGES.PROFILE_MANAGER.REMIT.MIN_APPROVERS')}
        />
      </span>

      <Row className="remit-modal__row__separeted">
        {lastIndex ? (
          <AdvancedButton
            startIcon={<PlusIcon width={10} height={10} />}
            height="1.838rem"
            type="button"
            onClick={() => addRow(index)}
            text={translate('PAGES.PROFILE_MANAGER.REMIT.NEW_ROW')}
            disabled={!newRow}
          />
        ) : null}
        {lastIndex && rows.length > 1 ? (
          <button
            style={{ backgroundColor: 'transparent', border: 'none' }}
            disabled={rows.length === 1}
            onClick={() => deleteRow(index)}
          >
            <TrashIcon width={14} height={14} />
          </button>
        ) : null}
      </Row>
    </Row>
  );
};

export default Form;
